import { createWebHistory, createRouter } from "vue-router";
import api from "../config/axios.js";
import store from "../store";
import { landingForUser } from '@/services/registrationExperience';
import OnboardingPage from '@/pages/Onboarding/OnboardingPage.vue';
import { ACCESS_PAGES, canAccessPage, firstAllowedPath, PUBLIC_CONTENT_PATHS, PUBLIC_PATHS } from "../utils/accessControl";
import { isStorefrontPath } from '@/utils/catalogRoutes';

import HomePage from "../pages/Home/HomePage.vue";
import DashboardPage from "../pages/Dashboard/DashboardPage.vue";
import CalendarPage from "../pages/Calendar/CalendarPage.vue";
import AttendantsDayPage from "../pages/Calendar/AttendantsDayPage.vue";
import PaymentPage from "../pages/Payment/PaymentPage.vue";
import LoginPage from "../pages/Authentication/LoginPage.vue";
import RegisterPage from "../pages/Authentication/RegisterPage.vue";
import ForgotPasswordPage from "../pages/Authentication/ForgotPasswordPage.vue";
import ForgotPasswordSendEmailPage from "../pages/Authentication/ForgotPasswordSendEmailPage.vue";
import TermsPage from "../pages/TermsPage.vue";
import PrivacyPolicyPage from "../pages/PrivacyPolicyPage.vue";
import ErrorPage from "../pages/ErrorPage.vue";
import ClientsPage from "../pages/Clients/ClientsPage.vue";
import ProductsPage from "../pages/Products/ProductsPage.vue";
import SalesPage from "../pages/Sales/SalesPage.vue";
import FinancialManagementPage from "../pages/Financial/FinancialManagementPage.vue";
import PerformancePage from "../pages/Performance/PerformancePage.vue";
import Configurations from "../pages/Configuration/ConfigurationPage.vue";
import ChatPage from "../pages/Chat/ChatPage.vue";
import AccessDeniedPage from "../pages/AccessDeniedPage.vue";

const routes = [
  { path: '/whatsapp-campaigns', name: 'WhatsappCampaignsPage', component: () => import('../pages/Campaigns/CampaignsPage.vue'), meta: { accessKey: 'whatsapp-campaigns' } },
  { path: '/onboarding', name: 'OnboardingPage', component: OnboardingPage },
  {
    path: "/",
    name: "HomePage",
    component: HomePage,
  },
  {
    path: "/dashboard",
    name: "DashboardPage",
    component: DashboardPage,
    meta: { accessKey: "dashboard" },
  },
  {
    path: "/performance",
    name: "PerformancePage",
    component: PerformancePage,
    meta: { accessKey: "performance" },
  },
  {
    path: "/calendar",
    name: "CalendarPage",
    component: CalendarPage,
    meta: { accessKey: "calendar" },
  },
  {
    path: "/attendants-day",
    name: "AttendantsDayPage",
    component: AttendantsDayPage,
    meta: { accessKey: "attendants-day" },
  }, 
  {
    path: "/chat",
    name: "ChatPage",
    component: ChatPage,
    meta: { accessKey: "chat" },
  },
  {
    path: "/payment",
    name: "PaymentPage",
    component: PaymentPage,
    meta: { accessKey: "payment" },
  },
  {
    path: "/clients",
    name: "ClientsPage",
    component: ClientsPage,
    meta: { accessKey: "clients" },
  },
  {
    path: "/products",
    name: "ProductsPage",
    component: ProductsPage,
    meta: { accessKey: "products" },
  },
  {
    path: "/sales",
    name: "SalesPage",
    component: SalesPage,
    meta: { accessKey: "sales" },
  },
  {
    path: "/financial-management",
    name: "FinancialManagementPage",
    component: FinancialManagementPage,
    meta: { accessKey: "financial-management" },
  },
  {
    path: "/configurations",
    name: "Configurations",
    component: Configurations,
    meta: { accessKey: "configurations" },
  },
  {
    path: "/access-denied",
    name: "AccessDeniedPage",
    component: AccessDeniedPage,
  },
  {
    path: "/login",
    name: "LoginPage",
    component: LoginPage,
  },
  {
    path: "/register",
    name: "RegisterPage",
    component: RegisterPage,
  },
  {
    path: "/forgot-password",
    name: "ForgotPasswordPage",
    component: ForgotPasswordPage,
  },
  {
    path: "/send-email-forgot-password",
    name: "ForgotPasswordSendEmailPage",
    component: ForgotPasswordSendEmailPage,
  },
  {
    path: "/terms",
    name: "TermsPage",
    component: TermsPage,
  },
  {
    path: "/privacy-policy",
    name: "PrivacyPolicyPage",
    component: PrivacyPolicyPage,
  },
  {
    path: '/:loja([a-z0-9-]{3,60})',
    component: () => import('@/pages/Catalog/StorefrontLayout.vue'),
    meta: { storefront: true },
    beforeEnter: (to) => isStorefrontPath(to.path) ? true : { name: 'ErrorPage', params: { pathMatch: to.path.slice(1).split('/') } },
    children: [
      { path: '', name: 'StorefrontHome', component: () => import('@/pages/Catalog/StorefrontHome.vue') },
      { path: 'catalogo', name: 'StorefrontCatalog', component: () => import('@/pages/Catalog/StorefrontCatalog.vue') },
      { path: 'produto/:id(\\d+)', name: 'StorefrontProduct', component: () => import('@/pages/Catalog/StorefrontProduct.vue') },
    ],
  },
  {
    path: "/:pathMatch(.*)*",
    name: "ErrorPage",
    component: ErrorPage
  }
];

const router = createRouter({
  history: createWebHistory(),
  linkExactActiveClass: "active",
  routes: routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }

    if (to.hash) {
      return {
        el: to.hash,
        top: 86,
        behavior: "smooth",
      };
    }

    return { top: 0, behavior: "smooth" };
  },
});

router.beforeEach(async (to, from) => {
  if (to.meta.storefront && isStorefrontPath(to.path)) return true;
  if (to.path === from.path && to.hash && to.hash !== from.hash) {
    return true;
  }

  const isPublicRoute = PUBLIC_PATHS.includes(to.path);

  if (PUBLIC_CONTENT_PATHS.includes(to.path)) {
    return true;
  }

  if (to.path === "/access-denied") {
    return true;
  }

  try {
    const response = await api.axios.get("/api/user");
    store.commit("setAuthenticatedUser", response.data);
    const allowedPages = Array.isArray(response.data?.allowed_pages) ? response.data.allowed_pages : [];

    if (isPublicRoute) {
      return landingForUser(response.data);
    }

    if (to.path === '/onboarding' && (!response.data?.onboarding?.eligible || !response.data?.is_company_admin || response.data?.company_active === false)) {
      return response.data?.company_active === false ? '/payment' : firstAllowedPath(allowedPages);
    }

    const accessKey = to.meta?.accessKey as string | undefined;
    if (!canAccessPage(allowedPages, accessKey)) {
      const page = ACCESS_PAGES.find((item) => item.key === accessKey);

      return {
        path: "/access-denied",
        query: {
          module: page?.label || "este modulo",
          key: accessKey || "",
        },
      };
    }

    return true;
  } catch (error) {
    const status = (error as { response?: { status?: number } }).response?.status;

    if (status === 401) {
      return isPublicRoute ? true : "/login";
    }

    if (status === 403) {
      const accessKey = to.meta?.accessKey as string | undefined;
      const page = ACCESS_PAGES.find((item) => item.key === accessKey);

      return isPublicRoute ? true : {
        path: "/access-denied",
        query: {
          module: page?.label || "este modulo",
          key: accessKey || "",
        },
      };
    }

    return isPublicRoute ? true : "/access-denied";
  }
});

export default router;
